import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';

import {
  AdLabel,
  AdInfoLabel,
  Location,
  PlacementHeadline,
  PlacementDescription,
  PlacementAction,
  Rating,
} from '../shared-components';

import { PlacementCard } from './ad-placement-card';

export const ImageAdWithLogoPlacementBase = ({
  placementData,
  customClassName,
  imagePreviewHeight,
  logoImagePreviewHeight,

  showShortHeadline,
  shortHeadline,
  isMultiLineShortHeadline,

  showLongHeadline,
  longHeadline,
  isMultiLineLongHeadline,

  finalUrl,
  showUrl,
  displayUrl,

  showDescription,
  description,
  isMultiLineDescription,

  showCallToAction,
  callToAction,

  showLocation,
  locationText,
  showPrice,
  priceText,

  showAdInfoLabel,
  isAdInfoLabelWithText,

  showRating,
  ratingPoint,
  maxPoint,
  showRatingPoint,
  ratingComment,

  i18n,
  classes,
}) => {
  const { placementMeta, imageMeta, logoImageMeta } = placementData || {};
  const { previewHeight, logoPreviewHeight, aspectRatio } = placementMeta || {};
  if (!placementData || !logoImageMeta) {
    return null;
  }

  const finalImagePreviewHeight =
    imagePreviewHeight > 0 ? imagePreviewHeight : previewHeight;

  const finalLogoPreviewHeight =
    logoImagePreviewHeight > 0 ? logoImagePreviewHeight : logoPreviewHeight;

  return (
    <PlacementCard
      url={finalUrl}
      placementCardClassName={classnames(
        classes.placementCard,
        customClassName
      )}
    >
      <div className={classnames(classes.placementImageContainer)}>
        {imageMeta && imageMeta.url && (
          <CroppedImageView
            resizeImageHeight={finalImagePreviewHeight}
            aspectRatio={aspectRatio}
            src={imageMeta.url}
            originalWidth={imageMeta.originalWidth}
            originalHeight={imageMeta.originalHeight}
          />
        )}
      </div>
      <div className={classnames(classes.logoContainer)}>
        {logoImageMeta.url && (
          <div className={classnames(classes.logoImageContainer)}>
            <CroppedImageView
              resizeImageHeight={finalLogoPreviewHeight}
              aspectRatio={1}
              src={logoImageMeta.url}
              originalWidth={logoImageMeta.originalWidth}
              originalHeight={logoImageMeta.originalHeight}
            />
          </div>
        )}
        <div className={classnames(classes.logoTextContainer)}>
          {showShortHeadline && (
            <PlacementHeadline
              headlineText={shortHeadline}
              isMultiLine={isMultiLineShortHeadline}
              placementHeadlineClassName={classnames(
                classes.placementShortHeadline,
                'short-headline'
              )}
            />
          )}
          {showUrl && (
            <div className={classnames(classes.displayUrlContainer)}>
              <AdLabel i18n={i18n} adsLabelClassName={classes.placementAdsLabel} />
              <span className="display-url">{displayUrl}</span>
            </div>
          )}
          {showRating && (
            <Rating
              i18n={i18n}
              ratingPoint={ratingPoint}
              maxPoint={maxPoint}
              showRatingPoint={showRatingPoint}
              commentText={ratingComment}
            />
          )}
        </div>
      </div>
      <div
        className={classnames(classes.placementTextContainer, 'text-container')}
      >
        {showLongHeadline && (
          <PlacementHeadline
            headlineText={longHeadline}
            isMultiLine={isMultiLineLongHeadline}
            placementHeadlineClassName={classnames(
              classes.placementLongHeadline,
              'long-headline'
            )}
          />
        )}
        {showPrice && (
          <span className={classnames(classes.priceText, 'price-text')}>
            {priceText}
          </span>
        )}
        {showLocation && (
          <Location showDetailLocation={false} locationText={locationText} />
        )}
        {showDescription && (
          <PlacementDescription
            description={description}
            isMultiLine={isMultiLineDescription}
            placementDescriptionClassName={classnames(
              classes.placementDescription,
              'description'
            )}
          />
        )}
        {showCallToAction &&
          callToAction && (
            <PlacementAction
              actionText={callToAction.replace(/([A-Z])/g, ' $1')}
              placementActionClassName={classes.placementCallToAction}
            />
          )}
        {showAdInfoLabel &&
          <AdInfoLabel adInfoLabelClassName={classes.adInfoLabel} showAdText={isAdInfoLabelWithText} />}
      </div>
    </PlacementCard>
  );
};

ImageAdWithLogoPlacementBase.propTypes = {
  placementData: PropTypes.shape({}).isRequired,
  customClassName: PropTypes.string,
  imagePreviewHeight: PropTypes.number,
  logoImagePreviewHeight: PropTypes.number,

  showShortHeadline: PropTypes.bool,
  shortHeadline: PropTypes.string,
  isMultiLineShortHeadline: PropTypes.bool,

  showLongHeadline: PropTypes.bool,
  longHeadline: PropTypes.string,
  isMultiLineLongHeadline: PropTypes.bool,

  finalUrl: PropTypes.string,
  showUrl: PropTypes.bool,
  displayUrl: PropTypes.string,

  showDescription: PropTypes.bool,
  description: PropTypes.string,
  isMultiLineDescription: PropTypes.bool,

  showCallToAction: PropTypes.bool,
  callToAction: PropTypes.string,

  showAdInfoLabel: PropTypes.bool,
  isAdInfoLabelWithText: PropTypes.bool,

  showRating: PropTypes.bool,
  ratingPoint: PropTypes.number,
  maxPoint: PropTypes.number,
  showRatingPoint: PropTypes.bool,
  ratingComment: PropTypes.string,

  showLocation: PropTypes.bool,
  locationText: PropTypes.string,
  showPrice: PropTypes.bool,
  priceText: PropTypes.string,

  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    placementCard: PropTypes.string,
    placementImageContainer: PropTypes.string,
    placementTextContainer: PropTypes.string,
    placementShortHeadline: PropTypes.string,
    placementLongHeadline: PropTypes.string,
    placementDescription: PropTypes.string,
    placementCallToAction: PropTypes.string,
    logoContainer: PropTypes.string,
    logoImageContainer: PropTypes.string,
    logoTextContainer: PropTypes.string,
    displayUrlContainer: PropTypes.string,
    priceText: PropTypes.string,
    placementAdsLabel: PropTypes.string,
    adInfoLabel: PropTypes.string,
  }).isRequired,
};

ImageAdWithLogoPlacementBase.defaultProps = {
  customClassName: '',
  imagePreviewHeight: -1,
  logoImagePreviewHeight: -1,

  showShortHeadline: false,
  showLongHeadline: false,
  showDescription: false,
  showCallToAction: false,
  showAdInfoLabel: false,
  isAdInfoLabelWithText: false,
  showRating: false,
  showRatingPoint: false,
  showLocation: false,
  showPrice: false,
  showUrl: false,

  ratingPoint: 5,
  maxPoint: 5,

  shortHeadline: undefined,
  isMultiLineShortHeadline: false,
  longHeadline: undefined,
  isMultiLineLongHeadline: false,
  finalUrl: undefined,
  displayUrl: undefined,
  description: undefined,
  isMultiLineDescription: false,
  callToAction: undefined,
  ratingComment: undefined,
  locationText: undefined,
  priceText: undefined,
};
